/* eslint-disable no-debugger */
import React, { useState, useEffect } from "react";
import withLanguageContext from "lib/hocs/withLanguageContext";
import { createRoot } from "react-dom/client";
import config from "lib/config";
import { Button } from "Components/Base/Inputs/Button";
import moment from "moment";
import styled from "styled-components";
import { POST } from "Common/api";
import { Alert } from "Components/Base/Feedback/Alert";

let disableErrors = false;
let selfLimit = 3;
const REPORT_API = "/v1/errors/report-js";
const DATE_FORMAT =
  config && config.dateFormat && config.dateFormat.toUpperCase();

const StyledNotification = styled.div`
  width: 90%;
  position: relative;
  h1 {
    font-size: 18px;
  }

  svg {
    display: none;
  }

  .errorinfo strong {
    text-transform: capitalize;
  }

  align-items: flex-start;

  .errorinfo {
    font-size: 14px;
  }
  .column div {
    margin: 3px 0;
  }

  @media only screen and (max-width: 700px) {
    .lc-notification {
      position: fixed;
      top: 0;
      left: 0;
      height: 100vh;
      width: 100vw;
      margin: 0;
      align-items: flex-start;
    }

    .lc-notification .errorinfo {
      width: 80vw;
      word-break: break-word;
    }
  }
`;

const InfoDiv = styled.div`
  .MuiAlert-message {
    display: initial !important;
  }

  .MuiAlert-root {
    width: 75%;
  }
`;

export default processError;

window.onerror = (msg, url, line, col, err) =>
  processError(
    (err && err.stack && `${err.message}\n\n${err.stack}`) ||
      (err && err.message) ||
      err ||
      msg,
  );
window.onunhandledrejection = processError;
window.addEventListener("unhandledrejection", processError);

function processError(errorParam) {
  if (disableErrors) return;

  let error = errorParam;
  while (error && (error.reason || error.detail)) {
    error = error.reason || error.detail;
  }

  const message = String(
    (error && error.stack) ||
      (error && error.message) ||
      error ||
      "Script error.",
  );
  if (error && error.stack) console.error(error.stack);

  const pageUrl = document.location.href.replace(/^https?:\/\/[^/]+/i, "");

  if (config.logErrorsJS && error !== "Script error.") {
    POST(REPORT_API, {}, { error, pageUrl })
      .then((res) => showErrorPage(error, encodeMessage(message), String(res)))
      .catch((ex) => {
        if (selfLimit) {
          // Never try to report a report-error more than 3 times...
          selfLimit--;
          processError(ex);
          showErrorPage(ex, encodeMessage(message), null);
        } else {
          selfLimit = 3;
        }
      });
  } else if (error.response) {
    // do not show if only javascript error, must be a response from server
    showErrorPage(error, message, null);
  }
}

function encodeMessage(msg) {
  const b64 = btoa(String(msg));
  return b64.replace(/(.{32})/g, "$1\n");
}

function hideLoader() {
  const loaderElement = document.querySelector("#loader");

  if (loaderElement) {
    loaderElement.classList.add("hidden");
  }
}

const ErrorNotification = (props) => {
  const { languageContext, errorId } = props;
  const isHTMLOutput = errorId.toLowerCase().indexOf("<!doctype html") > -1;
  const [showdetails, setShowDetails] = useState(false);
  const [ready, toggleReady] = useState(true);

  const clientData = {
    timestamp: moment().format(DATE_FORMAT),
    errorId,
    vendor: navigator.vendor,
    appVersion: navigator.appVersion,
    platform: navigator.platform,
    language: navigator.language,
    href: document.location.href,
    screen: {},
  };

  if (window.screen) {
    clientData.screen = {
      availWidth: window.screen.availWidth,
      availHeight: window.screen.availHeight,
      width: window.screen.width,
      height: window.screen.height,
      availLeft: window.screen.availLeft,
      availTop: window.screen.availTop,
      orientation: window.screen.orientation
        ? window.screen.orientation.type
        : "-",
    };
  }

  const errorInfo = messageText(clientData);

  useEffect(() => {
    hideLoader();
  }, []);

  function messageText(data) {
    return `${languageContext("errorinfo")}
  
  ${languageContext("errortimestamplabel")}: ${data.timestamp}
  ${languageContext("errorhead")}-ID: ${data.errorId}
  ${languageContext("pageurl")}: ${data.href}
  ${languageContext("vendor")}: ${data.vendor}
  ${languageContext("appversion")}: ${data.appVersion}
  ${languageContext("platform")}: ${data.platform}
  ${languageContext("language")}: ${data.language}
  ${languageContext("screen")}: ${Object.keys(data.screen).map(
    (key) => `${key}: ${data.screen[key]}`,
  )}  
  `;
  }

  function hideError() {
    const errorPageElement = document.getElementById("errorpage");

    if (errorPageElement) {
      errorPageElement.parentNode.removeChild(errorPageElement);
    }
    disableErrors = false;
    toggleReady(false);
  }

  const toggleDetails = () => {
    setShowDetails(!showdetails);
  };

  if (!ready) return <div />;

  return (
    <StyledNotification>
      <InfoDiv>
        <Alert
          severity="info"
          message={
            <>
              <div className="flex flex-space-b align-c">
                <h1>{languageContext("errorpageheader")}</h1>
                <Button
                  variant="outlined"
                  color="default"
                  onClick={hideError}
                  label="X"
                />
                <br />
              </div>
              <div className="animated fadeInDown">
                {!isHTMLOutput && (
                  <div
                    dangerouslySetInnerHTML={{
                      __html: languageContext("supporterrorinfo"),
                    }}
                  />
                )}
                <br />
                <Button
                  variant="text"
                  onClick={toggleDetails}
                  label={languageContext("clickfortechnicalinfo")}
                />

                {showdetails && (
                  <>
                    <hr />
                    <br />
                    <div className="errorinfo">
                      <div>
                        <strong>
                          {languageContext("errortimestamplabel")}:
                        </strong>
                        &nbsp;{clientData.timestamp}
                      </div>

                      {!isHTMLOutput && (
                        <div>
                          <strong>{languageContext("errorhead")}-ID:</strong>
                          &nbsp;
                          {errorId || "N/A"}
                        </div>
                      )}
                      <div>
                        <strong>{languageContext("pageurl")}:</strong>
                        &nbsp;
                        {clientData.href}
                      </div>
                      <div>
                        <strong>{languageContext("vendor")}:</strong>
                        &nbsp;
                        {clientData.vendor}
                      </div>
                      <div>
                        <strong>{languageContext("appversion")}:</strong>
                        &nbsp;
                        {clientData.appVersion}
                      </div>
                      <div>
                        <strong>{languageContext("platform")}:</strong>
                        &nbsp;
                        {clientData.platform}
                      </div>
                      <div>
                        <strong>{languageContext("language")}:</strong>
                        &nbsp;
                        {clientData.language}
                      </div>
                      <div>
                        &nbsp;
                        <div className="flex flex-wrap">
                          <strong>{languageContext("screen")}:</strong>
                          {Object.keys(clientData.screen).map((key, index) => (
                            <span key={index}>
                              &nbsp;{key}: {clientData.screen[key]}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <br />
                    <div className="flex flex-end">
                      <Button onClick={() => copyToClipboard(errorInfo)}>
                        {languageContext("copyerrorinfo")}
                      </Button>
                    </div>
                  </>
                )}
              </div>
              <br />
            </>
          }
        />
      </InfoDiv>
    </StyledNotification>
  );
};

const ErrorPage = withLanguageContext(ErrorNotification, "Error");

const copyToClipboard = (str) => {
  const el = document.createElement("textarea");
  el.value = str;
  el.setAttribute("readonly", "");
  el.style.position = "absolute";
  el.style.left = "-9999px";
  document.body.appendChild(el);
  el.select();
  document.execCommand("copy");
  document.body.removeChild(el);
};

function showErrorPage(error) {
  if (disableErrors) return;
  if (
    config.showErrorsJS &&
    document.body &&
    document.body.classList.contains("react-spa")
  ) {
    disableErrors = true;
    const child = document.createElement("div");
    child.id = "errorpage";
    document.body.appendChild(child);
    const errorText = error.response ? error.response.text : "N/A";

    const root = createRoot(child);
    root.render(<ErrorPage errorId={errorText} />);
  }
}
